import React from 'react';

import { View } from '@rbilabs/universal-components';
import { isObject } from 'lodash-es';
import { useIntl } from 'react-intl';

import { USE_GLOBAL_APP_MODALS_IN_CARD_MODAL } from 'components/card-modal';
import Dialog from 'components/dialog';
import { ModalAuth } from 'components/modal-auth';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { CurbsideConfirmationModal } from 'components/top-service-mode/curbside-confirmation-modal';
import { useAuthContext } from 'state/auth';
import { ModalAuthTrigger } from 'state/auth/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OnboardingContent } from 'state/onboarding';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';

/** put modals that should be globally available in the app here */
export const GlobalAppModals = ({ isModal = false }) => {
  const { modalAuthState } = useAuthContext();

  const {
    isPreConfirmCurbside,
    recent: { unavailablePendingRecentItem, setUnavailablePendingRecentItem },
  } = useOrderContext();

  const {
    isUpdateModalOpen,
    shouldShowPreConfirmCurbsideModal,
    setShouldShowPreConfirmCurbsideModal,
  } = useUIContext();

  const onboardingEnabled = useFlag(LaunchDarklyFlag.ENABLE_FEATURE_ONBOARDING);

  const curbsideModalDismiss = () => {
    setShouldShowPreConfirmCurbsideModal(false);
  };

  return (
    <>
      <View testID="app-modals-layout">
        {unavailablePendingRecentItem && (
          <RemoveUnavailableItemsModal
            unavailableCartEntries={[unavailablePendingRecentItem]}
            onConfirmCb={() => setUnavailablePendingRecentItem(null)}
          />
        )}
      </View>
      <StoreOrderingUnavailableDialog />
      {getModalAuthComponent(modalAuthState.trigger, isModal)}
      {!isUpdateModalOpen && onboardingEnabled && <OnboardingContent />}
      {!isUpdateModalOpen && shouldShowPreConfirmCurbsideModal && isPreConfirmCurbside() && (
        <CurbsideConfirmationModal showDialog onDismiss={curbsideModalDismiss} />
      )}
    </>
  );
};

const StoreOrderingUnavailableDialog = React.memo(() => {
  const {
    orderingUnavailableOpen,
    enableOrdering,
    onConfirmLocateRestaurants,
    onDismissStoreUnavailable,
  } = useStoreContext();
  const { formatMessage } = useIntl();
  if (!orderingUnavailableOpen) {
    return null;
  }

  return (
    <Dialog
      showDialog
      heading={
        isObject(orderingUnavailableOpen) && orderingUnavailableOpen.headingOverride
          ? orderingUnavailableOpen.headingOverride
          : formatMessage({ id: 'storeClosed' })
      }
      body={
        isObject(orderingUnavailableOpen) && orderingUnavailableOpen?.bodyOverride
          ? orderingUnavailableOpen?.bodyOverride
          : formatMessage({
              id: enableOrdering ? 'cannotPlaceOrder' : 'closeMessageToFindNearbyRestaurants',
            })
      }
      confirmButtonLabel={formatMessage({ id: 'findRestaurants' })}
      confirmDialog={onConfirmLocateRestaurants}
      onDismiss={onDismissStoreUnavailable}
      modalAppearanceEventMessage="Ordering is unavailable"
      isErrorModal
    />
  );
});

// only applicable to very nuanced iOS deep link, no auth situations
// needed where we can't have auth displaying on two screens (modal and non modal) at once
function getModalAuthComponent(trigger: ModalAuthTrigger | undefined, isModal: boolean) {
  if (!trigger) {
    return null;
  }

  if (
    !USE_GLOBAL_APP_MODALS_IN_CARD_MODAL &&
    (trigger === ModalAuthTrigger.Modal || trigger === ModalAuthTrigger.Global)
  ) {
    return <ModalAuth />;
  }

  if (trigger === ModalAuthTrigger.Global && !isModal) {
    return <ModalAuth />;
  }

  if (trigger === ModalAuthTrigger.Modal && isModal) {
    return <ModalAuth />;
  }
  return null;
}
